import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, ArrowRight, MapPin, X, Gift, Zap, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [isFiring, setIsFiring] = useState(false);

  const handleFire = () => {
    setIsFiring(true);
    const audio = new Audio('https://www.soundjay.com/mechanical/gun-gunshot-01.mp3');
    audio.volume = 0.2;
    audio.play().catch(() => {});
    setTimeout(() => setIsFiring(false), 600); 
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-black text-center pt-24">
      {/* --- BULLET STREAK EFFECT --- */}
      <AnimatePresence>
        {isFiring && (
          <motion.div
            initial={{ x: "-100vw", opacity: 1 }}
            animate={{ x: "100vw", opacity: 0 }}
            transition={{ duration: 0.3, ease: "linear" }}
            className="absolute top-1/2 left-0 w-full h-[2px] z-[100] pointer-events-none"
          >
            <div className="h-full w-80 bg-gradient-to-r from-transparent via-red-500 to-white blur-[1px]" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- BACKGROUND LAYER --- */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/40 to-black z-10" />
        <motion.img 
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
          src="/images/hero-shooting.png" 
          alt="Shooting Range Background - Olympic Standard Facility" 
          className="w-full h-full object-cover opacity-50" 
          loading="lazy"
          decoding="async"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1599819811279-d5ad9cccf838?q=80&w=2070&auto=format&fit=crop"; }}
        />
      </div>

      {/* --- CONTENT LAYER --- */}
      <div className="relative z-20 max-w-5xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-3 px-6 py-2.5 rounded-full bg-white/5 border border-white/10 mb-10 backdrop-blur-md"
        >
          <Target size={14} className="text-red-600 animate-pulse" />
          <span className="text-[10px] font-black tracking-[0.5em] uppercase text-zinc-300">
            NRAI Standard Range • Sector 81
          </span>
        </motion.div>

        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-black text-white uppercase italic tracking-tighter mb-8 leading-[0.85]"
        >
          CHAITANYA <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-400 to-zinc-700">
            SHOOTING ACADEMY
          </span>
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-zinc-400 text-sm md:text-lg max-w-2xl mx-auto mb-14 font-medium tracking-wide leading-relaxed"
        >
          Precision coaching meets world-class infrastructure. Start your journey toward national excellence with Olympic-standard target systems.
        </motion.p>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Link 
            to="/book-demo" 
            onClick={handleFire}
            className="group bg-red-600 hover:bg-white hover:text-black text-white px-16 py-6 rounded-full font-black text-xs uppercase tracking-[0.3em] transition-all inline-flex items-center gap-4 shadow-[0_0_50px_rgba(185,28,28,0.4)] active:scale-95"
          >
            Schedule Trial Session <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
          </Link>
        </motion.div>

        {/* --- CHAMPION GLORY TRUST BADGE --- */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mt-8 flex items-center justify-center"
        >
          <a 
            href="#achievements" 
            className="inline-flex items-center gap-2.5 px-6 py-2.5 rounded-full bg-zinc-900/90 border border-gold/40 hover:border-gold text-white font-bold text-[10px] sm:text-xs uppercase tracking-wider backdrop-blur-md transition-all hover:scale-105 shadow-[0_0_25px_rgba(255,215,0,0.15)] group"
          >
            <Trophy size={14} className="text-gold animate-bounce" />
            <span>Home of Champions: <strong className="text-gold">🥈 Silver & 🥉 Bronze Medalists</strong> • <strong className="text-yellow-300">🎯 Pre-National Qualifiers</strong></span>
            <ArrowRight size={12} className="text-gold group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>

      {/* --- HUB MARKER --- */}
      <div className="absolute bottom-12 right-12 z-20 hidden lg:block">
        <div className="flex items-center gap-5 bg-black/40 backdrop-blur-2xl p-6 rounded-[2rem] border border-white/5 shadow-2xl">
          <div className="w-12 h-12 bg-red-600/10 rounded-2xl flex items-center justify-center text-red-600 border border-red-600/20">
            <MapPin size={22} />
          </div>
          <div className="text-left">
            <p className="text-white font-black text-[12px] uppercase italic leading-none">Gurugram Center</p>
            <p className="text-[9px] text-zinc-500 uppercase tracking-[0.2em] mt-2 font-bold font-heading">Near Vipul Lavanya</p>
          </div>
        </div>
      </div>
    </section>
  );
}